/* Le lecteur de carte du domaine.

   Une carte figée ne situe rien: on voyait Caux et des parcelles, sans savoir
   si l'on était près de Pézenas, de Béziers ou de la mer. Il faut pouvoir
   s'éloigner, se rapprocher, et revenir.

   La carte se construit au chargement, sans que le visiteur ait à la demander.
   Un premier essai la cachait derrière un bouton, par prudence: à tort. Une
   réponse de tuile OpenStreetMap ne pose aucun cookie, et l'article 82 de la
   loi Informatique et Libertés ne vise que la lecture ou l'écriture
   d'informations dans le terminal, pas une requête vers un serveur. Le
   consentement n'était donc pas exigé, et le clic supplémentaire ne protégeait
   personne. Il en irait autrement d'une carte Google, qui dépose ses traceurs.

   L'affiche statique reste dessous et n'est jamais retirée: si le script
   échoue ou n'est pas exécuté, la page garde une carte.

   Écrit ici plutôt qu'emprunté à une bibliothèque: il s'agit d'une grille de
   tuiles, d'un glisser et de quatre boutons.

   L'adresse des tuiles n'est pas écrite ici mais lue sur l'élément: la
   politique d'usage d'OpenStreetMap demande de pouvoir changer de fournisseur
   sans toucher au code.

   Données © les contributeurs OpenStreetMap, sous ODbL. L'attribution affichée
   sur la carte n'est pas décorative, elle est la condition de la licence.
*/
(() => {
  const TUILE = 256;
  const RAD = Math.PI / 180;

  // Web Mercator, en pixels du monde entier au niveau de zoom demandé.
  const lonEnX = (lon, z) => ((lon + 180) / 360) * Math.pow(2, z) * TUILE;
  const latEnY = (lat, z) =>
    ((1 - Math.asinh(Math.tan(lat * RAD)) / Math.PI) / 2) * Math.pow(2, z) * TUILE;

  const borner = (v, min, max) => Math.min(Math.max(v, min), max);

  function lecteur(figure) {
    const scene = figure.querySelector('[data-carte-scene]');
    if (!scene) return;

    const LAT = parseFloat(figure.dataset.lat);
    const LON = parseFloat(figure.dataset.lon);
    const GABARIT = figure.dataset.tuiles;
    const Z_DEPART = parseInt(figure.dataset.zoom, 10);
    const Z_MIN = parseInt(figure.dataset.zoomMin, 10);
    const Z_MAX = parseInt(figure.dataset.zoomMax, 10);
    // Assez large pour qu'on atteigne Montpellier, Béziers et la mer sans
    // buter: des bornes serrées donnent l'impression que le glisser est cassé.
    const CADRE = {
      nord: LAT + 1.1, sud: LAT - 1.1,
      ouest: LON - 1.5, est: LON + 1.5,
    };

    let zoom = Z_DEPART;
    let centreX = lonEnX(LON, zoom);
    let centreY = latEnY(LAT, zoom);
    const tuiles = new Map();
    let attente = null;

    scene.insertAdjacentHTML('beforeend', `
      <div class="fdo-carte__vue" data-carte-vue tabindex="0" role="application"
           aria-label="Carte du domaine. Flèches pour se déplacer, touches plus et moins pour zoomer.">
        <div class="fdo-carte__couche" data-carte-couche></div>
        <span class="fdo-carte__marqueur" data-carte-marqueur aria-hidden="true"></span>
        <div class="fdo-carte__outils">
          <button type="button" data-carte-plus aria-label="Zoomer" title="Zoomer">+</button>
          <button type="button" data-carte-moins aria-label="Dézoomer" title="Dézoomer">&minus;</button>
          <button type="button" data-carte-recentrer aria-label="Revenir sur le domaine" title="Revenir sur le domaine">◎</button>
          <button type="button" data-carte-plein aria-label="Afficher en plein écran" title="Afficher en plein écran">⤢</button>
        </div>
        <p class="fdo-carte__doigt" data-carte-doigt>Touchez la carte pour la déplacer</p>
        <p class="fdo-carte__invite" data-carte-invite hidden>Ctrl + molette pour zoomer</p>
        <p class="fdo-carte__credit">
          <a href="https://www.openstreetmap.org/copyright" rel="noopener nofollow" target="_blank">©&nbsp;OpenStreetMap</a>
        </p>
      </div>`);

    const vue = scene.querySelector('[data-carte-vue]');
    const couche = scene.querySelector('[data-carte-couche]');
    const marqueur = scene.querySelector('[data-carte-marqueur]');
    const invite = scene.querySelector('[data-carte-invite]');
    const plus = scene.querySelector('[data-carte-plus]');
    const moins = scene.querySelector('[data-carte-moins]');

    // Si la vue est plus large que le cadre autorisé, le centre s'y verrouille
    // au lieu de flotter: sans cela, la carte partirait en butée dès le premier
    // glissement au zoom le plus large.
    function contraindre() {
      const l = vue.clientWidth, h = vue.clientHeight;
      const xMin = lonEnX(CADRE.ouest, zoom), xMax = lonEnX(CADRE.est, zoom);
      const yMin = latEnY(CADRE.nord, zoom), yMax = latEnY(CADRE.sud, zoom);
      centreX = xMax - xMin <= l ? (xMin + xMax) / 2 : borner(centreX, xMin + l / 2, xMax - l / 2);
      centreY = yMax - yMin <= h ? (yMin + yMax) / 2 : borner(centreY, yMin + h / 2, yMax - h / 2);
    }

    function dessiner() {
      attente = null;
      contraindre();
      const l = vue.clientWidth, h = vue.clientHeight;
      const gauche = centreX - l / 2, haut = centreY - h / 2;
      const n = Math.pow(2, zoom);
      const vues = new Set();

      // Une tuile de marge tout autour: sans elle, les tuiles n'arrivent qu'au
      // moment où le bord entre dans le cadre, et le glissement s'accompagne
      // d'une bande vide qui se remplit après coup.
      for (let x = Math.floor(gauche / TUILE) - 1; x <= Math.floor((gauche + l) / TUILE) + 1; x++) {
        for (let y = Math.floor(haut / TUILE) - 1; y <= Math.floor((haut + h) / TUILE) + 1; y++) {
          if (y < 0 || y >= n) continue;
          const xMonde = ((x % n) + n) % n;
          const cle = `${zoom}/${x}/${y}`;
          vues.add(cle);
          let img = tuiles.get(cle);
          if (!img) {
            img = new Image(TUILE, TUILE);
            img.className = 'fdo-carte__tuile';
            img.alt = '';
            img.decoding = 'async';
            // une image est glissable par défaut, et le navigateur emportait la
            // tuile au lieu de laisser la carte se déplacer
            img.draggable = false;
            img.src = GABARIT.replace('{z}', zoom).replace('{x}', xMonde).replace('{y}', y);
            couche.appendChild(img);
            tuiles.set(cle, img);
          }
          img.style.transform = `translate3d(${x * TUILE - gauche}px, ${y * TUILE - haut}px, 0)`;
        }
      }
      tuiles.forEach((img, cle) => {
        if (!vues.has(cle)) { img.remove(); tuiles.delete(cle); }
      });

      marqueur.style.transform =
        `translate3d(${lonEnX(LON, zoom) - gauche}px, ${latEnY(LAT, zoom) - haut}px, 0)`;
      moins.disabled = zoom <= Z_MIN;
      plus.disabled = zoom >= Z_MAX;
    }

    // Le glissement redessine à chaque image plutôt qu'à chaque événement de
    // pointeur: sur un pavé tactile ils arrivent bien plus vite que l'écran ne
    // rafraîchit, et on recalculait la grille pour rien.
    const redessiner = () => { if (attente === null) attente = requestAnimationFrame(dessiner); };

    // Zoome en gardant sous le curseur le point qu'il désignait. Sans cela, on
    // vise un lieu-dit, on zoome, et il a glissé hors de l'écran.
    function zoomer(pas, ancreX, ancreY) {
      const cible = borner(zoom + pas, Z_MIN, Z_MAX);
      if (cible === zoom) return;
      const l = vue.clientWidth, h = vue.clientHeight;
      const ax = ancreX === undefined ? l / 2 : ancreX;
      const ay = ancreY === undefined ? h / 2 : ancreY;
      const f = Math.pow(2, cible - zoom);
      centreX = (centreX + ax - l / 2) * f - (ax - l / 2);
      centreY = (centreY + ay - h / 2) * f - (ay - h / 2);
      zoom = cible;
      redessiner();
    }

    plus.addEventListener('click', () => zoomer(1));
    moins.addEventListener('click', () => zoomer(-1));
    scene.querySelector('[data-carte-recentrer]').addEventListener('click', () => {
      zoom = Z_DEPART;
      centreX = lonEnX(LON, zoom);
      centreY = latEnY(LAT, zoom);
      redessiner();
    });

    const plein = scene.querySelector('[data-carte-plein]');
    plein.addEventListener('click', () => {
      if (document.fullscreenElement) document.exitFullscreen();
      else if (scene.requestFullscreen) scene.requestFullscreen();
    });
    document.addEventListener('fullscreenchange', () => {
      const dedans = document.fullscreenElement === scene;
      const libelle = dedans ? 'Quitter le plein écran' : 'Afficher en plein écran';
      plein.setAttribute('aria-label', libelle);
      plein.setAttribute('title', libelle);
      plein.textContent = dedans ? '✕' : '⤢';
      redessiner();
    });

    vue.addEventListener('dblclick', (e) => {
      const r = vue.getBoundingClientRect();
      zoomer(1, e.clientX - r.left, e.clientY - r.top);
    });

    // La molette seule fait défiler la page, comme partout ailleurs: piéger le
    // défilement sur une carte au milieu d'une page la rend difficile à
    // quitter. Elle affiche alors la manœuvre au lieu de ne rien faire, ce qui
    // se lisait comme une carte cassée. En plein écran il n'y a plus de page
    // derrière, la molette zoome directement.
    let minuterie = null;
    vue.addEventListener('wheel', (e) => {
      const dedans = document.fullscreenElement === scene;
      if (!dedans && !e.ctrlKey && !e.metaKey) {
        invite.hidden = false;
        clearTimeout(minuterie);
        minuterie = setTimeout(() => { invite.hidden = true; }, 1600);
        return;
      }
      e.preventDefault();
      const r = vue.getBoundingClientRect();
      zoomer(e.deltaY < 0 ? 1 : -1, e.clientX - r.left, e.clientY - r.top);
    }, { passive: false });

    vue.addEventListener('keydown', (e) => {
      const pas = { ArrowLeft: [-80, 0], ArrowRight: [80, 0], ArrowUp: [0, -80], ArrowDown: [0, 80] }[e.key];
      if (pas) { e.preventDefault(); centreX += pas[0]; centreY += pas[1]; redessiner(); return; }
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomer(1); }
      if (e.key === '-') { e.preventDefault(); zoomer(-1); }
    });

    const pointeurs = new Map();
    let ecart = 0;
    function distance() {
      const [a, b] = [...pointeurs.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    }
    vue.addEventListener('pointerdown', (e) => {
      // Les boutons sont dans la vue, et capturer le pointeur ici détournait
      // les événements suivants: le relâchement n'arrivait plus sur le bouton,
      // donc aucun clic n'était synthétisé et ni le zoom ni le recentrage ne
      // partaient. Les outils gardent leurs propres événements.
      if (e.target.closest('.fdo-carte__outils')) return;

      // Au doigt, le premier contact arme la carte sans la déplacer: jusque là
      // le défilement de la page traverse. Sans ce sas, une carte pleine
      // largeur retient le doigt et on ne peut plus quitter la page. En plein
      // écran la question ne se pose pas.
      if (e.pointerType === 'touch' && !vue.classList.contains('est-armee')
          && document.fullscreenElement !== scene) {
        vue.classList.add('est-armee');
        return;
      }
      // Sans quoi le navigateur lance son propre glisser-déposer sur la tuile
      // ou sélectionne le texte de la page: on emportait une image au lieu de
      // déplacer la carte.
      e.preventDefault();
      vue.setPointerCapture(e.pointerId);
      pointeurs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointeurs.size === 2) ecart = distance();
    });
    vue.addEventListener('pointermove', (e) => {
      const avant = pointeurs.get(e.pointerId);
      if (!avant) return;
      const dx = e.clientX - avant.x, dy = e.clientY - avant.y;
      pointeurs.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointeurs.size === 1) {
        centreX -= dx; centreY -= dy; redessiner();
      } else if (pointeurs.size === 2 && ecart) {
        // Le zoom reste par paliers entiers: un pincement continu obligerait à
        // mettre la grille de tuiles à l'échelle, pour un gain que cette carte
        // ne demande pas. Le palier se déclenche au franchissement.
        const suivant = distance();
        if (suivant / ecart > 1.6) { zoomer(1); ecart = suivant; }
        else if (suivant / ecart < 0.625) { zoomer(-1); ecart = suivant; }
      }
    });
    const relacher = (e) => { pointeurs.delete(e.pointerId); if (pointeurs.size < 2) ecart = 0; };
    vue.addEventListener('pointerup', relacher);
    vue.addEventListener('pointercancel', relacher);
    document.addEventListener('pointerdown', (e) => {
      if (!vue.contains(e.target)) vue.classList.remove('est-armee');
    });

    window.addEventListener('resize', redessiner);
    figure.classList.add('fdo-carte--vivante');
    dessiner();
  }

  document.querySelectorAll('[data-carte]').forEach(lecteur);
})();
